import { useEffect, useState } from "react";
import { isValidSelector } from "../helper";
import { useApp } from "../context/AppState";

const PLACEHOLDER_SLOT_ATTR = "data-weupsell-embed-slot";

const useEmbedding = (embeddingData = {}, popupId) => {
  const [placeholder, setPlaceholder] = useState(null);
  const { appState } = useApp();

  const slotId =
    popupId != null && popupId !== ""
      ? String(popupId)
      : `${embeddingData?.css_selector || "embed"}-${embeddingData?.position || "slot"}`;

  const observeDomMutations = () => {
    const targetNode = document.body;
    const config = {
      childList: true,
      subtree: true,
      attributes: true,
    };

    const observer = new MutationObserver(() => {
      const { is_embedded, css_selector, position } = embeddingData;
      if (!is_embedded || !css_selector || !isValidSelector(css_selector)) return;

      const targetSection = document.querySelector(css_selector);
      if (targetSection) {
        insertPlaceholder(targetSection, position, slotId);
      }
    });

    observer.observe(targetNode, config);
    return observer;
  };

  const insertPlaceholder = (targetSection, position, embedSlotId) => {
    const existingPlaceholder = document.querySelector(
      `[${PLACEHOLDER_SLOT_ATTR}="${embedSlotId}"]`
    );

    if (existingPlaceholder) {
      setPlaceholder(existingPlaceholder);
      return;
    }

    const newPlaceholder = document.createElement("div");
    newPlaceholder.setAttribute(PLACEHOLDER_SLOT_ATTR, embedSlotId);
    newPlaceholder.setAttribute("aim-data-placeholder", "true");
    newPlaceholder.style.width = "100%";
    newPlaceholder.style.height = "max-content";
    newPlaceholder.style.display = "block";

    switch (position) {
      case "beforebegin":
        targetSection.insertAdjacentElement("beforebegin", newPlaceholder);
        break;
      case "afterbegin":
        targetSection.insertAdjacentElement("afterbegin", newPlaceholder);
        break;
      case "beforeend":
        targetSection.insertAdjacentElement("beforeend", newPlaceholder);
        break;
      case "afterend":
        targetSection.insertAdjacentElement("afterend", newPlaceholder);
        break;
      default:
        targetSection.insertAdjacentElement("afterend", newPlaceholder);
        break;
    }

    setPlaceholder(newPlaceholder);
  };

  useEffect(() => {
    const { is_embedded, css_selector, position } = embeddingData;

    if (!is_embedded) return;

    let targetSection = null;

    if (appState?.is_preview_mode) {
      targetSection = document.getElementById("preview-container");
    } else if (css_selector && isValidSelector(css_selector)) {
      targetSection = document.querySelector(css_selector);
    }

    if (targetSection) {
      insertPlaceholder(targetSection, position, slotId);
    }

    const observer = observeDomMutations();

    return () => {
      observer.disconnect();
    };
  }, [embeddingData, appState?.is_preview_mode, slotId]);

  return {
    placeholder,
  };
};

export default useEmbedding;
